import {
  Box,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs'
import { SalesVolumeTable } from 'components/domain/account/SalesVolumeTable'
import { V2MigrationCheckDialog } from 'components/domain/backoffice/V2MigrationCheckDialog'
import { Button, CloseIcon } from 'components/facets'
import { TablePaginationControls } from 'components/facets/TablePaginationControls'
import {
  AdminTransactionsExport,
  CardtraderLaunchStatus,
  disablePricingBotBeta,
  downloadAdminCardmarketStockExport,
  enablePricingBotBeta,
  getAdminCardmarketAccounts,
  getAdminSalesVolumes,
  getAdminTransactionsExport,
  getAdminTransactionsExports,
  getCardtraderLaunchStatus,
  getJobStatus,
  getPricingBotBetaStatus,
  moveToV2,
  runCardmarketOrderFetch,
  runCardtraderAlignment,
  runCardtraderSync,
  startAdminCardmarketStockExport,
  startAdminTransactionsExport,
  toggleAdminShopify,
  toggleAdminTaxation,
  unlinkCardmarket,
  unlinkCardtrader,
  updateUserCurrency,
  updateUserEmail,
  wipePowertoolsInventory,
} from 'lib/api'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import {
  AppCurrency,
  ChannelFilterOptions,
  formatDateToYYYYMMDD,
  gameMetaData,
  IdGame,
  SUPPORTED_CURRENCIES,
} from 'shared'

const formatDate = (d: string | Date | undefined) => {
  if (!d) return ''
  const date = new Date(d)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

// Date + time for the CT status timestamps; '—' when never set (never run, or reset by a failure).
const formatDateTime = (d: string | null | undefined): string => {
  if (!d) return '—'
  return new Date(d).toLocaleString()
}

// Games to pick from when exporting a user's Cardmarket stock. Generic accessories (sleeves,
// boxes, ...) belong to no game: Cardmarket stock calls take idGame 99 for them, same as the
// exportUserStock task does.
const STOCK_EXPORT_GAMES = [
  ...gameMetaData.map((game) => ({ idGame: game.idGame as number, name: game.name })),
  { idGame: 99, name: 'Generic accessories' },
]

// Human-readable reason(s) why a Cardtrader alignment/sync launch is currently blocked.
const describeCtBlock = (status: CardtraderLaunchStatus): string => {
  const reasons: string[] = []
  if (status.alignmentRunning) reasons.push('an alignment is already running')
  if (status.syncRunning) reasons.push('a sync is already running')
  if (status.pendingTransactions > 0) {
    reasons.push(
      `${status.pendingTransactions} pending CardTrader transaction${
        status.pendingTransactions === 1 ? '' : 's'
      }`
    )
  }
  if (status.onboardingTransactions > 0) {
    reasons.push(
      `${status.onboardingTransactions} pending onboarding transaction${
        status.onboardingTransactions === 1 ? '' : 's'
      }`
    )
  }
  return reasons.join(', ')
}

export interface UserDetailsDrawerProps {
  open: boolean
  onClose: () => void
  data: any[] | null
  loading: boolean
  userId: string
  email: string
  currency: AppCurrency
  shopifyEnabled: boolean
  taxationEnabled: boolean
  isV2: boolean
  cardtraderUserName?: string
  onEmailUpdated?: (newEmail: string) => void
  onCurrencyUpdated?: (currency: AppCurrency) => void
}

export const UserDetailsDrawer = ({
  open,
  onClose,
  data,
  loading,
  userId,
  email: initialEmail,
  currency: initialCurrency,
  shopifyEnabled: initialShopifyEnabled,
  taxationEnabled: initialTaxationEnabled,
  isV2: initialIsV2,
  cardtraderUserName: initialCardtraderUserName,
  onEmailUpdated,
  onCurrencyUpdated,
}: UserDetailsDrawerProps): JSX.Element => {
  const { enqueueSnackbar } = useSnackbar()
  const [email, setEmail] = useState(initialEmail)
  const [editingEmail, setEditingEmail] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [emailLoading, setEmailLoading] = useState(false)
  const [currency, setCurrency] = useState(initialCurrency)
  const [currencyLoading, setCurrencyLoading] = useState(false)
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string | null>(null)
  const [salesVolumes, setSalesVolumes] = useState<any | null>(null)
  const [salesVolumesLoading, setSalesVolumesLoading] = useState(false)
  const [shopifyEnabled, setShopifyEnabled] = useState(initialShopifyEnabled)
  const [shopifyLoading, setShopifyLoading] = useState(false)
  const [taxationEnabled, setTaxationEnabled] = useState(initialTaxationEnabled)
  const [taxationLoading, setTaxationLoading] = useState(false)
  const [isV2, setIsV2] = useState(initialIsV2)
  const [v2Loading, setV2Loading] = useState(false)
  const [v2ConfirmOpen, setV2ConfirmOpen] = useState(false)
  const [pricingBotBetaEnabled, setPricingBotBetaEnabled] = useState(false)
  const [pricingBotBetaHasPlans, setPricingBotBetaHasPlans] = useState(false)
  const [pricingBotBetaHasBackup, setPricingBotBetaHasBackup] = useState(false)
  const [pricingBotBetaLoading, setPricingBotBetaLoading] = useState(false)
  const [pricingBotBetaEnableConfirmOpen, setPricingBotBetaEnableConfirmOpen] = useState(false)
  const [pricingBotBetaRestoreConfirmOpen, setPricingBotBetaRestoreConfirmOpen] = useState(false)
  const [wipeConfirmOpen, setWipeConfirmOpen] = useState(false)
  const [wipeLoading, setWipeLoading] = useState(false)
  const [cardtraderUserName, setCardtraderUserName] = useState(initialCardtraderUserName)
  const [cardtraderLoading, setCardtraderLoading] = useState(false)
  const [cardtraderConfirmOpen, setCardtraderConfirmOpen] = useState(false)
  const [ctActionType, setCtActionType] = useState<'alignment' | 'sync' | null>(null)
  const [ctCheckLoading, setCtCheckLoading] = useState(false)
  const [ctStatus, setCtStatus] = useState<CardtraderLaunchStatus | null>(null)
  const [ctLaunching, setCtLaunching] = useState(false)
  const [cardmarketAccounts, setCardmarketAccounts] = useState<
    { username: string; professional: boolean; currency: string }[]
  >([])
  const [cardmarketAccountsLoading, setCardmarketAccountsLoading] = useState(false)
  const [cardmarketUnlinkingAccount, setCardmarketUnlinkingAccount] = useState<string | null>(null)
  const [cardmarketConfirmAccount, setCardmarketConfirmAccount] = useState<string | null>(null)
  const [stockExportIdGame, setStockExportIdGame] = useState<number>(IdGame.Magic)
  const [stockExportRunning, setStockExportRunning] = useState(false)
  const [v2CheckOpen, setV2CheckOpen] = useState(false)
  const [orderFetchLimitDate, setOrderFetchLimitDate] = useState('')
  const [orderFetchStarting, setOrderFetchStarting] = useState(false)
  const [transactionsExportFrom, setTransactionsExportFrom] = useState('')
  const [transactionsExportTo, setTransactionsExportTo] = useState(formatDateToYYYYMMDD(new Date()))
  const [transactionsExportChannel, setTransactionsExportChannel] = useState<string>(
    ChannelFilterOptions.powertools
  )
  const [transactionsExportType, setTransactionsExportType] = useState('quantity')
  const [transactionsExportStarting, setTransactionsExportStarting] = useState(false)
  const [transactionsExports, setTransactionsExports] = useState<AdminTransactionsExport[]>([])
  const [transactionsExportsPage, setTransactionsExportsPage] = useState(1)
  const [transactionsExportsHasNextPage, setTransactionsExportsHasNextPage] = useState(false)

  useEffect(() => {
    setEmail(initialEmail)
  }, [initialEmail])

  useEffect(() => {
    setCurrency(initialCurrency)
  }, [initialCurrency])

  useEffect(() => {
    setShopifyEnabled(initialShopifyEnabled)
  }, [initialShopifyEnabled])

  useEffect(() => {
    setTaxationEnabled(initialTaxationEnabled)
  }, [initialTaxationEnabled])

  useEffect(() => {
    setIsV2(initialIsV2)
  }, [initialIsV2])

  useEffect(() => {
    if (!open || !userId) return
    getPricingBotBetaStatus(userId)
      .then(({ enabled, hasPricingPlans, hasBackup }) => {
        setPricingBotBetaEnabled(enabled)
        setPricingBotBetaHasPlans(hasPricingPlans)
        setPricingBotBetaHasBackup(hasBackup)
      })
      .catch(() => {
        enqueueSnackbar('Failed to check pricing bot beta status', { variant: 'error' })
        setPricingBotBetaEnabled(false)
        setPricingBotBetaHasPlans(false)
        setPricingBotBetaHasBackup(false)
      })
  }, [open, userId])

  useEffect(() => {
    setCardtraderUserName(initialCardtraderUserName)
  }, [initialCardtraderUserName])

  useEffect(() => {
    if (!open || !userId) return
    setCardmarketAccountsLoading(true)
    getAdminCardmarketAccounts(userId)
      .then(setCardmarketAccounts)
      .catch(() => setCardmarketAccounts([]))
      .finally(() => setCardmarketAccountsLoading(false))
  }, [open, userId])

  useEffect(() => {
    if (!open || !userId) return
    loadTransactionsExports()
  }, [open, userId, transactionsExportsPage])

  // the list is the status: reloaded until every export has ended
  useEffect(() => {
    if (!open || !transactionsExports.some((e) => !e.exportEndedAt)) return
    const timeout = setTimeout(loadTransactionsExports, 5000)
    return () => clearTimeout(timeout)
  }, [open, transactionsExports])

  const handleUpdateEmail = async () => {
    if (!newEmail || newEmail === email) {
      setEditingEmail(false)
      return
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(newEmail)) {
      enqueueSnackbar('Enter a valid email address (e.g. name@domain.com)', { variant: 'error' })
      return
    }
    setEmailLoading(true)
    try {
      await updateUserEmail(userId, newEmail)
      setEmail(newEmail)
      onEmailUpdated?.(newEmail)
      setEditingEmail(false)
      setNewEmail('')
      enqueueSnackbar('Email updated successfully', { variant: 'success' })
    } catch (err: any) {
      const reason = err?.response?.data?.error
      enqueueSnackbar(reason ? `Failed to update email: ${reason}` : 'Failed to update email', {
        variant: 'error',
      })
    } finally {
      setEmailLoading(false)
    }
  }
  const handleUpdateCurrency = async (newCurrency: AppCurrency) => {
    setCurrencyLoading(true)
    try {
      await updateUserCurrency(userId, newCurrency)
      setCurrency(newCurrency)
      onCurrencyUpdated?.(newCurrency)
      enqueueSnackbar('Currency updated successfully', { variant: 'success' })
    } catch (err: any) {
      const reason = err?.response?.data?.error
      enqueueSnackbar(
        reason ? `Failed to update currency: ${reason}` : 'Failed to update currency',
        {
          variant: 'error',
        }
      )
    } finally {
      setCurrencyLoading(false)
    }
  }

  // When the CT action dialog opens, check whether a launch is currently allowed before
  // unlocking "Proceed" (pending transactions, a running job, or pending onboarding all block it).
  useEffect(() => {
    if (!ctActionType) return
    setCtCheckLoading(true)
    setCtStatus(null)
    getCardtraderLaunchStatus(userId)
      .then(setCtStatus)
      .catch(() => {
        enqueueSnackbar('Failed to check CardTrader launch status', { variant: 'error' })
        setCtStatus(null)
      })
      .finally(() => setCtCheckLoading(false))
  }, [ctActionType, userId])

  // Ongoing periods have no MonthlySubscription row behind them (id null), so Rails has no sales
  // volumes to return for one — they are display-only.
  const handleRowClick = async (subscriptionId: string) => {
    if (selectedSubscriptionId === subscriptionId) {
      setSelectedSubscriptionId(null)
      setSalesVolumes(null)
      return
    }
    setSelectedSubscriptionId(subscriptionId)
    setSalesVolumesLoading(true)
    setSalesVolumes(null)
    try {
      const data = await getAdminSalesVolumes(userId, subscriptionId)
      setSalesVolumes(data)
    } catch {
      setSalesVolumes(null)
    } finally {
      setSalesVolumesLoading(false)
    }
  }

  const handleMoveToV2 = async () => {
    setV2ConfirmOpen(false)
    setV2Loading(true)
    try {
      await moveToV2(userId)
      setIsV2(true)
    } catch {
      // ignore
    } finally {
      setV2Loading(false)
    }
  }

  const handleEnablePricingBotBeta = async () => {
    setPricingBotBetaEnableConfirmOpen(false)
    setPricingBotBetaLoading(true)
    try {
      const result = await enablePricingBotBeta(userId)
      setPricingBotBetaEnabled(true)
      setPricingBotBetaHasBackup(result.plansCount > 0)
      enqueueSnackbar(
        result.plansCount === 0
          ? 'Pricing bot beta enabled (user has no plans yet)'
          : `Pricing bot beta enabled (${result.plansCount} plan${
              result.plansCount === 1 ? '' : 's'
            } flattened)`,
        { variant: 'success' }
      )
    } catch {
      enqueueSnackbar('Could not enable pricing bot beta', { variant: 'error' })
    } finally {
      setPricingBotBetaLoading(false)
    }
  }

  const handleDisablePricingBotBeta = async () => {
    setPricingBotBetaRestoreConfirmOpen(false)
    setPricingBotBetaLoading(true)
    try {
      const result = await disablePricingBotBeta(userId)
      setPricingBotBetaEnabled(false)
      if (!result.hadBackup) {
        enqueueSnackbar(
          `Pricing bot beta disabled — no backup on file, kept ${result.keptCount} current plan${
            result.keptCount === 1 ? '' : 's'
          }`,
          { variant: 'warning' }
        )
      } else {
        enqueueSnackbar(
          `Pricing bot beta disabled (${result.restoredCount} restored, ${result.keptCount} kept)`,
          { variant: 'success' }
        )
      }
    } catch {
      enqueueSnackbar('Could not restore pricing plans', { variant: 'error' })
    } finally {
      setPricingBotBetaLoading(false)
    }
  }

  const handleWipeInventory = async () => {
    setWipeConfirmOpen(false)
    setWipeLoading(true)
    try {
      await wipePowertoolsInventory(userId)
    } catch {
      // ignore
    } finally {
      setWipeLoading(false)
    }
  }

  const handleUnlinkCardmarket = async () => {
    const account = cardmarketConfirmAccount
    setCardmarketConfirmAccount(null)
    if (!account) return
    setCardmarketUnlinkingAccount(account)
    try {
      await unlinkCardmarket(userId, account)
      setCardmarketAccounts((prev) => prev.filter((a) => a.username !== account))
    } catch {
      // ignore
    } finally {
      setCardmarketUnlinkingAccount(null)
    }
  }

  // The Cardmarket fetch runs as a BullMQ job that takes minutes, so we follow its state and pull
  // the CSV only once it is done. Bounded on purpose: a failed child job can leave the parent
  // sitting in 'waiting-children' indefinitely, and without a cap the spinner would never stop.
  const waitForStockExportJob = async (jobId: string): Promise<string> => {
    const pollIntervalMs = 3000
    const maxAttempts = 200 // ~10 minutes

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs))
      const status = await getJobStatus(jobId)
      if (status === 'completed' || status === 'failed') {
        return String(status)
      }
    }

    return 'timeout'
  }

  const handleDownloadCardmarketStock = async () => {
    setStockExportRunning(true)
    try {
      const { jobId } = await startAdminCardmarketStockExport(userId, stockExportIdGame)
      const status = await waitForStockExportJob(jobId)

      if (status !== 'completed') {
        enqueueSnackbar(
          status === 'timeout'
            ? 'The Cardmarket export is still running after 10 minutes: check the job queue'
            : 'The Cardmarket export job failed',
          { variant: 'error' }
        )
        return
      }

      const res = await downloadAdminCardmarketStockExport(userId, jobId, stockExportIdGame)
      const gameName =
        STOCK_EXPORT_GAMES.find((game) => game.idGame === stockExportIdGame)?.name ??
        stockExportIdGame

      const blob = new Blob([res.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `export-${userId}-${gameName}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch {
      enqueueSnackbar('Could not export the Cardmarket stock', { variant: 'error' })
    } finally {
      setStockExportRunning(false)
    }
  }

  const loadTransactionsExports = () =>
    getAdminTransactionsExports(userId, transactionsExportsPage)
      .then(({ transactionCsvExports, hasNextPage }) => {
        setTransactionsExports(transactionCsvExports)
        setTransactionsExportsHasNextPage(hasNextPage)
      })
      .catch(() => enqueueSnackbar('Could not load the transactions exports', { variant: 'error' }))

  const handleStartTransactionsExport = async () => {
    setTransactionsExportStarting(true)
    try {
      await startAdminTransactionsExport(
        userId,
        transactionsExportFrom,
        transactionsExportTo,
        transactionsExportChannel,
        transactionsExportType
      )
      if (transactionsExportsPage === 1) loadTransactionsExports()
      else setTransactionsExportsPage(1)
    } catch {
      enqueueSnackbar('Could not start the transactions export', { variant: 'error' })
    } finally {
      setTransactionsExportStarting(false)
    }
  }

  const handleDownloadTransactionsExport = async (transactionsExport: AdminTransactionsExport) => {
    const res = await getAdminTransactionsExport(transactionsExport.id)
    if (res.status !== 'ready') return

    const blob = new Blob([res.csvData], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `transactions-${userId}-${transactionsExport.params.channel}-${transactionsExport.params.exportType}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const handleRunCardmarketOrderFetch = async () => {
    setOrderFetchStarting(true)
    try {
      await runCardmarketOrderFetch(userId, orderFetchLimitDate || undefined)
      enqueueSnackbar('Cardmarket order fetch started', { variant: 'success' })
    } catch {
      enqueueSnackbar('Could not start the Cardmarket order fetch', { variant: 'error' })
    } finally {
      setOrderFetchStarting(false)
    }
  }

  const handleUnlinkCardtrader = async () => {
    setCardtraderConfirmOpen(false)
    setCardtraderLoading(true)
    try {
      await unlinkCardtrader(userId)
      setCardtraderUserName(undefined)
    } catch {
      // ignore
    } finally {
      setCardtraderLoading(false)
    }
  }

  const closeCtDialog = () => {
    setCtActionType(null)
    setCtStatus(null)
  }

  const handleRunCardtraderAction = async () => {
    if (!ctActionType) return
    const label = ctActionType === 'alignment' ? 'CT Alignment' : 'CT Sync'
    const run = ctActionType === 'alignment' ? runCardtraderAlignment : runCardtraderSync
    setCtLaunching(true)
    try {
      const res = await run(userId)
      if (res.launched) {
        enqueueSnackbar(`${label} started`, { variant: 'success' })
        closeCtDialog()
      } else {
        // A blocking condition appeared between the check and the launch: keep the dialog open
        // and refresh it with the reason coming back from the backend.
        const refreshed = res.blocked !== undefined ? (res as CardtraderLaunchStatus) : null
        setCtStatus(refreshed)
        enqueueSnackbar(
          `Cannot start ${label}${refreshed ? `: ${describeCtBlock(refreshed)}` : ''}`,
          { variant: 'warning' }
        )
      }
    } catch {
      enqueueSnackbar(`Failed to start ${label}`, { variant: 'error' })
    } finally {
      setCtLaunching(false)
    }
  }

  const handleToggleShopify = async () => {
    setShopifyLoading(true)
    try {
      const result = await toggleAdminShopify(userId)
      setShopifyEnabled(result.shopify)
    } catch {
      // ignore
    } finally {
      setShopifyLoading(false)
    }
  }

  const handleToggleTaxation = async () => {
    setTaxationLoading(true)
    try {
      const result = await toggleAdminTaxation(userId)
      setTaxationEnabled(result.taxation)
      if (result.addedFields.length > 0) {
        enqueueSnackbar(
          `Feature flag enabled. The following fields were missing and added: ${result.addedFields.join(
            ', '
          )}`,
          { variant: 'warning' }
        )
      } else {
        enqueueSnackbar('Taxation enabled for this user', { variant: 'success' })
      }
    } catch {
      enqueueSnackbar('Could not enable Taxation', { variant: 'error' })
    } finally {
      setTaxationLoading(false)
    }
  }

  const handleClose = () => {
    setSelectedSubscriptionId(null)
    setSalesVolumes(null)
    setEditingEmail(false)
    setNewEmail('')
    onClose()
  }

  return (
    <Drawer anchor="right" open={open} onClose={handleClose} data-testid="user-details-drawer">
      <Box width={400} padding={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
          <h4>User Details</h4>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box marginBottom={3}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Email
          </Typography>
          {editingEmail ? (
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <TextField
                size="small"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleUpdateEmail()
                  if (e.key === 'Escape') {
                    setEditingEmail(false)
                    setNewEmail('')
                  }
                }}
                placeholder="New email"
                fullWidth
                autoFocus
                disabled={emailLoading}
              />
              {emailLoading && <CircularProgress size={20} />}
            </Box>
          ) : (
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography style={{ wordBreak: 'break-all' }}>{email}</Typography>
              <Button
                variant="contained"
                onClick={() => {
                  setNewEmail(email)
                  setEditingEmail(true)
                }}
              >
                Edit email
              </Button>
            </Box>
          )}
        </Box>

        <Box marginBottom={3}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Currency
          </Typography>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <TextField
              select
              size="small"
              data-testid="user-currency-select"
              value={currency}
              onChange={(e) => handleUpdateCurrency(e.target.value as AppCurrency)}
              disabled={currencyLoading}
              fullWidth
            >
              {SUPPORTED_CURRENCIES.map((c) => (
                <MenuItem key={c} value={c} data-testid={`currency-${c}`}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
            {currencyLoading && <CircularProgress size={20} />}
          </Box>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" paddingTop={4}>
            <CircularProgress />
          </Box>
        ) : data && data.length > 0 ? (
          <>
            <Box>
              <Typography variant="h5" color="textSecondary" gutterBottom>
                Subscription Periods
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Start</TableCell>
                    <TableCell align="center">End</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((subscription, index) => (
                    <TableRow
                      key={subscription.id ?? index}
                      hover={!subscription.ongoing}
                      onClick={
                        subscription.ongoing
                          ? undefined
                          : () => handleRowClick(String(subscription.id))
                      }
                      style={{
                        cursor: subscription.ongoing ? 'default' : 'pointer',
                        backgroundColor:
                          !subscription.ongoing &&
                          selectedSubscriptionId === String(subscription.id)
                            ? '#e3f2fd'
                            : undefined,
                      }}
                    >
                      <TableCell align="center">
                        {formatDate(subscription.subscriptionStart)}
                      </TableCell>
                      <TableCell align="center">
                        {formatDate(subscription.subscriptionEnd)}
                        {subscription.ongoing && (
                          <Chip
                            size="small"
                            label="Ongoing"
                            style={{ marginLeft: 8 }}
                            data-testid="ongoing-subscription-chip"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {selectedSubscriptionId && (
              <Box marginTop={3}>
                <SalesVolumeTable data={salesVolumes} loading={salesVolumesLoading} />
              </Box>
            )}
          </>
        ) : (
          <Typography color="textSecondary">No subscription history</Typography>
        )}

        {!loading && (
          <>
            <Box marginTop={4}>
              <Typography variant="h5" color="textSecondary" gutterBottom>
                Cardmarket
              </Typography>
              {cardmarketAccountsLoading ? (
                <CircularProgress size={20} />
              ) : cardmarketAccounts.length === 0 ? (
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography>No accounts connected</Typography>
                  <Button variant="contained" disabled>
                    Unlink
                  </Button>
                </Box>
              ) : (
                cardmarketAccounts.map((account) => (
                  <Box
                    key={account.username}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    marginBottom={1}
                  >
                    <Typography>{account.username}</Typography>
                    <Button
                      variant="contained"
                      onClick={() => setCardmarketConfirmAccount(account.username)}
                      disabled={cardmarketUnlinkingAccount === account.username}
                      style={{ backgroundColor: '#d32f2f', color: 'white' }}
                    >
                      {cardmarketUnlinkingAccount === account.username ? (
                        <CircularProgress size={20} />
                      ) : (
                        'Unlink'
                      )}
                    </Button>
                  </Box>
                ))
              )}

              <div className="d-flex align-items-center justify-content-between mt-3">
                <Box width={100}>
                  <TextField
                    select
                    fullWidth
                    label="Stock export"
                    className="stock-export-game-select"
                    value={stockExportIdGame}
                    onChange={(event) => setStockExportIdGame(Number(event.target.value))}
                    disabled={stockExportRunning}
                  >
                    {STOCK_EXPORT_GAMES.map((game) => (
                      <MenuItem key={game.idGame} value={game.idGame}>
                        {game.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                {stockExportRunning ? (
                  <CircularProgress size={20} />
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleDownloadCardmarketStock}
                    disabled={cardmarketAccounts.length === 0}
                  >
                    Download CSV
                  </Button>
                )}
              </div>

              <div className="d-flex justify-content-end mt-3">
                <Button
                  variant="outlined"
                  onClick={() => setV2CheckOpen(true)}
                  disabled={cardmarketAccounts.length === 0}
                >
                  Check migration to v2
                </Button>
              </div>

              <div className="d-flex align-items-center justify-content-between mt-3">
                <Box width={160}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Fetch orders since"
                    InputLabelProps={{ shrink: true }}
                    value={orderFetchLimitDate}
                    onChange={(event) => setOrderFetchLimitDate(event.target.value)}
                    disabled={orderFetchStarting}
                  />
                </Box>
                {orderFetchStarting ? (
                  <CircularProgress size={20} />
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleRunCardmarketOrderFetch}
                    disabled={cardmarketAccounts.length === 0}
                  >
                    Fetch Orders
                  </Button>
                )}
              </div>
            </Box>

            <Box marginTop={4}>
              <Typography variant="h5" color="textSecondary" gutterBottom>
                CardTrader
              </Typography>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography>
                  {cardtraderUserName ? cardtraderUserName : 'No accounts connected'}
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setCardtraderConfirmOpen(true)}
                  disabled={cardtraderLoading || !cardtraderUserName}
                  style={
                    cardtraderUserName ? { backgroundColor: '#d32f2f', color: 'white' } : undefined
                  }
                >
                  {cardtraderLoading ? <CircularProgress size={20} /> : 'Unlink'}
                </Button>
              </Box>
              {cardtraderUserName && (
                <Box display="flex" style={{ gap: 8, justifyContent: 'end' }} marginTop={1}>
                  <Button variant="contained" onClick={() => setCtActionType('alignment')}>
                    Run CT Alignment
                  </Button>
                  <Button variant="contained" onClick={() => setCtActionType('sync')}>
                    Run CT Sync
                  </Button>
                </Box>
              )}
            </Box>

            <Box marginTop={4}>
              <Typography variant="h5" color="textSecondary" gutterBottom>
                Transactions export
              </Typography>
              <Box display="flex" style={{ gap: 8 }}>
                <TextField
                  data-testid="transactions-export-from"
                  type="date"
                  label="From"
                  InputLabelProps={{ shrink: true }}
                  value={transactionsExportFrom}
                  onChange={(event) => setTransactionsExportFrom(event.target.value)}
                  disabled={transactionsExportStarting}
                />
                <TextField
                  type="date"
                  label="To"
                  InputLabelProps={{ shrink: true }}
                  value={transactionsExportTo}
                  onChange={(event) => setTransactionsExportTo(event.target.value)}
                  disabled={transactionsExportStarting}
                />
              </Box>
              <div className="d-flex align-items-center justify-content-between mt-3">
                <Box display="flex" style={{ gap: 8 }}>
                  <TextField
                    select
                    label="Channel"
                    value={transactionsExportChannel}
                    onChange={(event) => {
                      setTransactionsExportChannel(event.target.value)
                      // tax transactions exist only on powertools
                      if (event.target.value !== ChannelFilterOptions.powertools) {
                        setTransactionsExportType((type) => (type === 'tax' ? 'quantity' : type))
                      }
                    }}
                    disabled={transactionsExportStarting}
                  >
                    {Object.values(ChannelFilterOptions).map((channel) => (
                      <MenuItem key={channel} value={channel}>
                        {channel}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Type"
                    value={transactionsExportType}
                    onChange={(event) => setTransactionsExportType(event.target.value)}
                    disabled={transactionsExportStarting}
                  >
                    {['quantity', 'price', 'reservation', 'tax']
                      .filter(
                        (type) =>
                          type !== 'tax' ||
                          transactionsExportChannel === ChannelFilterOptions.powertools
                      )
                      .map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                  </TextField>
                </Box>
                {transactionsExportStarting ? (
                  <CircularProgress size={20} />
                ) : (
                  <Button
                    data-testid="transactions-export-btn"
                    variant="contained"
                    onClick={handleStartTransactionsExport}
                    disabled={!transactionsExportFrom || !transactionsExportTo}
                  >
                    Export
                  </Button>
                )}
              </div>
              {transactionsExports.length > 0 && (
                <Table size="small" className="mt-3" data-testid="transactions-exports-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Interval</TableCell>
                      <TableCell>Export</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactionsExports.map((transactionsExport) => (
                      <TableRow key={transactionsExport.id}>
                        <TableCell>
                          {formatDate(transactionsExport.intervalStartDate)} -{' '}
                          {formatDate(transactionsExport.intervalEndDate)}
                        </TableCell>
                        <TableCell>
                          {transactionsExport.params.channel} {transactionsExport.params.exportType}
                        </TableCell>
                        <TableCell>{formatDateTime(transactionsExport.createdAt)}</TableCell>
                        <TableCell align="center">
                          {transactionsExport.lastErrorAt ? (
                            <i
                              className="fa-light fa-circle-x error-color"
                              title={transactionsExport.lastError ?? ''}
                            />
                          ) : transactionsExport.exportEndedAt ? (
                            <i
                              data-testid="transactions-export-download"
                              className="fa-light fa-arrow-down-to-bracket table-action-icon"
                              onClick={() => handleDownloadTransactionsExport(transactionsExport)}
                              style={{ cursor: 'pointer' }}
                            />
                          ) : (
                            <CircularProgress size={16} />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {(transactionsExportsPage > 1 || transactionsExportsHasNextPage) && (
                <TablePaginationControls
                  currentPage={transactionsExportsPage}
                  setPage={setTransactionsExportsPage}
                  isLastPage={!transactionsExportsHasNextPage}
                  startFromZero={false}
                />
              )}
            </Box>

            <Box marginTop={4}>
              <Typography variant="h5" color="textSecondary" gutterBottom>
                Version
              </Typography>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography>Status: {isV2 ? 'v2' : 'v1'}</Typography>
                <Button
                  variant="contained"
                  onClick={() => setV2ConfirmOpen(true)}
                  disabled={v2Loading || isV2}
                >
                  {v2Loading ? <CircularProgress size={20} /> : 'Move to v2'}
                </Button>
              </Box>
            </Box>

            <Box marginTop={4}>
              <Typography variant="h5" color="textSecondary" gutterBottom>
                Shopify
              </Typography>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography>Status: {shopifyEnabled ? 'Enabled' : 'Disabled'}</Typography>
                <Button
                  variant="contained"
                  onClick={handleToggleShopify}
                  disabled={shopifyLoading || shopifyEnabled}
                >
                  {shopifyLoading ? <CircularProgress size={20} /> : 'Enable Shopify'}
                </Button>
              </Box>
            </Box>

            <Box marginTop={4}>
              <Typography variant="h5" color="textSecondary" gutterBottom>
                Taxation
              </Typography>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography>Status: {taxationEnabled ? 'Enabled' : 'Disabled'}</Typography>
                <Button
                  variant="contained"
                  onClick={handleToggleTaxation}
                  disabled={taxationLoading || taxationEnabled}
                >
                  {taxationLoading ? <CircularProgress size={20} /> : 'Enable Taxation'}
                </Button>
              </Box>
            </Box>

            <Box marginTop={4}>
              <Typography variant="h5" color="textSecondary" gutterBottom>
                Pricing Bot Beta
              </Typography>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography>Status: {pricingBotBetaEnabled ? 'Enabled' : 'Disabled'}</Typography>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <Button
                    variant="contained"
                    data-testid="enable-pricing-bot-beta-btn"
                    onClick={() => setPricingBotBetaEnableConfirmOpen(true)}
                    disabled={pricingBotBetaLoading || pricingBotBetaEnabled}
                  >
                    {pricingBotBetaLoading ? <CircularProgress size={20} /> : 'Enable Beta'}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setPricingBotBetaRestoreConfirmOpen(true)}
                    disabled={pricingBotBetaLoading || !pricingBotBetaEnabled}
                  >
                    {pricingBotBetaLoading ? <CircularProgress size={20} /> : 'Restore'}
                  </Button>
                </Box>
              </Box>
              {!pricingBotBetaEnabled && !pricingBotBetaHasPlans && (
                <Typography variant="caption" color="textSecondary">
                  User has no pricing plans — enabling will opt them into the beta with an empty
                  plan list
                </Typography>
              )}
              {pricingBotBetaEnabled && !pricingBotBetaHasBackup && (
                <Typography variant="caption" color="textSecondary">
                  No backup on file — restoring will keep the current plans and just turn the flag
                  off
                </Typography>
              )}
            </Box>

            <Box marginTop={4}>
              <Typography variant="h5" color="textSecondary" gutterBottom>
                Danger Zone
              </Typography>
              <Button
                variant="contained"
                onClick={() => setWipeConfirmOpen(true)}
                disabled={wipeLoading}
                style={{ backgroundColor: '#d32f2f', color: 'white' }}
              >
                {wipeLoading ? <CircularProgress size={20} /> : 'Wipe PowerTools Inventory'}
              </Button>
            </Box>
          </>
        )}
      </Box>

      <V2MigrationCheckDialog
        open={v2CheckOpen}
        onClose={() => setV2CheckOpen(false)}
        userId={userId}
      />

      <ClosableDialog open={v2ConfirmOpen} onClose={() => setV2ConfirmOpen(false)}>
        <Box margin={3}>
          <DialogTitle disableTypography>
            <h3>Confirm Move to v2</h3>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will move the user to v2 (PT Inventory). This operation cannot be easily
              reverted. Are you sure?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setV2ConfirmOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleMoveToV2} variant="contained">
              Confirm
            </Button>
          </DialogActions>
        </Box>
      </ClosableDialog>

      <ClosableDialog
        open={pricingBotBetaEnableConfirmOpen}
        onClose={() => setPricingBotBetaEnableConfirmOpen(false)}
      >
        <DialogTitle>Enable Pricing Bot Beta</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will back up the user&apos;s current pricingPlans into pricingPlansBackup, then
            flatten pricingPlans in place and enable the pricingBotBeta feature flag, opting them
            into the new pricing bot UI. Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPricingBotBetaEnableConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEnablePricingBotBeta}
            variant="contained"
            data-testid="confirm-enable-pricing-bot-beta-btn"
          >
            Confirm
          </Button>
        </DialogActions>
      </ClosableDialog>

      <ClosableDialog
        open={pricingBotBetaRestoreConfirmOpen}
        onClose={() => setPricingBotBetaRestoreConfirmOpen(false)}
      >
        <DialogTitle>Restore Pricing Plans</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This restores the plans that existed before the beta from pricingPlansBackup (recovering
            their original nested structure), keeps any plans the user created during the beta, and
            disables the pricingBotBeta feature flag — opting them back out of the new pricing bot
            UI. Edits made in the beta to a pre-existing plan are discarded. Use this when a user
            reports a problem after the rollout. Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPricingBotBetaRestoreConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDisablePricingBotBeta} variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </ClosableDialog>

      <ClosableDialog
        open={!!cardmarketConfirmAccount}
        onClose={() => setCardmarketConfirmAccount(null)}
      >
        <Box margin={3}>
          <DialogTitle disableTypography>
            <h3>Unlink Cardmarket Account</h3>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will unlink the Cardmarket account &quot;{cardmarketConfirmAccount}&quot; from
              this user. Are you sure?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setCardmarketConfirmAccount(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleUnlinkCardmarket}
              variant="contained"
              style={{ backgroundColor: '#d32f2f', color: 'white' }}
            >
              Unlink
            </Button>
          </DialogActions>
        </Box>
      </ClosableDialog>

      <ClosableDialog open={cardtraderConfirmOpen} onClose={() => setCardtraderConfirmOpen(false)}>
        <Box margin={3}>
          <DialogTitle disableTypography>
            <h3>Unlink CardTrader Account</h3>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will unlink the CardTrader account from this user. Are you sure?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setCardtraderConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUnlinkCardtrader}
              variant="contained"
              style={{ backgroundColor: '#d32f2f', color: 'white' }}
            >
              Unlink
            </Button>
          </DialogActions>
        </Box>
      </ClosableDialog>

      <ClosableDialog
        open={!!ctActionType}
        onClose={
          ctLaunching
            ? () => {
                console.log()
              }
            : closeCtDialog
        }
      >
        <Box margin={3}>
          <DialogTitle disableTypography>
            <h3>{ctActionType === 'sync' ? 'Run CT Sync' : 'Run CT Alignment'}</h3>
          </DialogTitle>
          <DialogContent>
            {ctStatus && (
              <div
                style={{ marginTop: 16, fontSize: 13, lineHeight: 1.7 }}
                className="text-muted mb-2"
              >
                <div className="d-flex flex-column">
                  <span>Alignment</span>
                  <span className="ms-2">
                    started: {formatDateTime(ctStatus.lastAlignmentStart)}{' '}
                  </span>
                  <span className="ms-2">ended: {formatDateTime(ctStatus.lastAlignmentEnd)}</span>
                </div>
                <div className="d-flex flex-column">
                  <span>Sync</span>
                  <span className="ms-2">started:{formatDateTime(ctStatus.lastSyncStart)}</span>
                  <span className="ms-2">ended: {formatDateTime(ctStatus.lastSyncEnd)}</span>
                </div>
              </div>
            )}

            {ctCheckLoading ? (
              <Box display="flex" alignItems="center" style={{ gap: 12 }} paddingY={1}>
                <CircularProgress size={20} />
                <span className="fw-semibold" style={{ marginBottom: 0 }}>
                  Checking CardTrader status…
                </span>
              </Box>
            ) : ctStatus === null ? (
              <span>Could not check CardTrader status. Close and try again.</span>
            ) : ctStatus.blocked ? (
              <span className="fw-semibold">
                Cannot run now: {describeCtBlock(ctStatus)}. Wait until it clears before running.
              </span>
            ) : (
              <span className="fw-semibold">
                No pending CardTrader transactions or running jobs. You can proceed.
              </span>
            )}
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={closeCtDialog} disabled={ctLaunching}>
              Cancel
            </Button>
            <Button
              onClick={handleRunCardtraderAction}
              variant="contained"
              disabled={ctCheckLoading || ctLaunching || !ctStatus || ctStatus.blocked}
            >
              {ctLaunching ? <CircularProgress size={20} /> : 'Proceed'}
            </Button>
          </DialogActions>
        </Box>
      </ClosableDialog>

      <ClosableDialog open={wipeConfirmOpen} onClose={() => setWipeConfirmOpen(false)}>
        <Box margin={3}>
          <DialogTitle disableTypography>
            <h3>Wipe PowerTools Inventory</h3>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will permanently set all location quantities and reserved quantities to zero for
              this user. This operation cannot be undone. Are you sure?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWipeConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={handleWipeInventory}
              variant="contained"
              style={{ backgroundColor: '#d32f2f', color: 'white' }}
            >
              Wipe Inventory
            </Button>
          </DialogActions>
        </Box>
      </ClosableDialog>
    </Drawer>
  )
}
